import { useEffect, useState, type FormEvent } from "react";
import type { ColorSemantico } from "../../../shared/types";
import { Boton } from "../../components/Boton";
import { Campo, Input, Select, TextArea } from "../../components/Formulario";
import { Insignia } from "../../components/Insignia";
import { Modal } from "../../components/Modal";
import { SelectorColorSemantico } from "../../components/SelectorColorSemantico";
import { Tabla, type ColumnaTabla } from "../../components/Tabla";
import { useToast } from "../../components/Toast";
import { api, ErrorApi } from "../../lib/apiClient";
import { resolverColorSemantico } from "../../lib/colorSemantico";

interface ItemCatalogo {
  id: string;
  nombre: string;
  orden: number;
  activo: boolean;
  color?: string | null;
  responsable_usuario_id?: string | null;
  descripcion?: string | null;
}

interface UsuarioBasico {
  id: string;
  nombre_completo: string;
}

// Genérico para Estados procesales / Valores estratégicos / Áreas de
// práctica: mismo shape base (id, nombre, orden, activo), mismo CRUD
// tenant-scoped bajo /api/catalogos-estudio/*. Ver
// migrations/0008_evaluacion_estrategia.sql y 0012_areas_practica.sql.
// `paletaControlada` cambia el color de hex libre (input type="color", el
// de Áreas de Práctica) a la grilla de 8 swatches del vocabulario
// controlado (ver migrations/0013_colores_semanticos.sql) — Estado Procesal
// la usa, Áreas de Práctica mantiene su color libre existente sin cambios.
export function PanelCatalogoEstudio({
  titulo,
  descripcion,
  endpoint,
  puedeAdministrar,
  mostrarColor,
  paletaControlada,
  mostrarResponsable,
  mostrarDescripcion,
}: {
  titulo: string;
  descripcion: string;
  endpoint: "estados-procesales" | "valores-estrategicos" | "areas-practica" | "tipos-actuacion" | "tipos-honorario";
  puedeAdministrar: boolean;
  mostrarColor?: boolean;
  paletaControlada?: boolean;
  mostrarResponsable?: boolean;
  mostrarDescripcion?: boolean;
}) {
  const { mostrar } = useToast();
  const [items, setItems] = useState<ItemCatalogo[]>([]);
  const [cargando, setCargando] = useState(true);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [enEdicion, setEnEdicion] = useState<ItemCatalogo | null>(null);
  const [valores, setValores] = useState({
    nombre: "",
    orden: "0",
    color: "#64748b",
    responsable_usuario_id: "",
    descripcion: "",
  });
  const [guardando, setGuardando] = useState(false);
  const [usuarios, setUsuarios] = useState<UsuarioBasico[]>([]);

  async function cargar() {
    setCargando(true);
    try {
      const datos = await api.get<{ items: ItemCatalogo[] }>(`/catalogos-estudio/${endpoint}`);
      setItems(datos.items);
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo cargar el catálogo.", "error");
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  // Sólo se necesita para resolver el nombre del Responsable — un rol que
  // ve este catálogo pero no tiene "usuarios.ver" (p. ej. ABOGADO) no debe
  // ver un toast de error por esto: se resuelve en silencio a "—".
  useEffect(() => {
    if (!mostrarResponsable) return;
    api
      .get<{ items: UsuarioBasico[] }>("/usuarios")
      .then((d) => setUsuarios(d.items))
      .catch(() => {});
  }, [mostrarResponsable]);

  function abrirAlta() {
    setEnEdicion(null);
    setValores({
      nombre: "",
      orden: String(items.length + 1),
      color: paletaControlada ? "" : "#64748b",
      responsable_usuario_id: "",
      descripcion: "",
    });
    setModalAbierto(true);
  }

  function abrirEdicion(item: ItemCatalogo) {
    setEnEdicion(item);
    setValores({
      nombre: item.nombre,
      orden: String(item.orden),
      color: item.color ?? (paletaControlada ? "" : "#64748b"),
      responsable_usuario_id: item.responsable_usuario_id ?? "",
      descripcion: item.descripcion ?? "",
    });
    setModalAbierto(true);
  }

  async function guardar(e: FormEvent) {
    e.preventDefault();
    setGuardando(true);
    try {
      const cuerpo = {
        nombre: valores.nombre,
        orden: Number(valores.orden),
        ...(mostrarColor ? { color: valores.color || null } : {}),
        ...(mostrarResponsable ? { responsable_usuario_id: valores.responsable_usuario_id || null } : {}),
        ...(mostrarDescripcion ? { descripcion: valores.descripcion || null } : {}),
      };
      if (enEdicion) {
        await api.put(`/catalogos-estudio/${endpoint}/${enEdicion.id}`, cuerpo);
      } else {
        await api.post(`/catalogos-estudio/${endpoint}`, cuerpo);
      }
      mostrar("Guardado correctamente.", "exito");
      setModalAbierto(false);
      cargar();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo guardar.", "error");
    } finally {
      setGuardando(false);
    }
  }

  async function alternarActivo(item: ItemCatalogo) {
    try {
      await api.patch(`/catalogos-estudio/${endpoint}/${item.id}/activo`, {
        activo: !item.activo,
      });
      cargar();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo cambiar el estado.", "error");
    }
  }

  const columnas: ColumnaTabla<ItemCatalogo>[] = [
    {
      encabezado: "Nombre",
      render: (i) =>
        mostrarColor ? (
          <span className="flex items-center gap-2">
            <span
              className={
                paletaControlada
                  ? `h-3 w-3 shrink-0 rounded-full ${resolverColorSemantico(i.color).punto}`
                  : "h-3 w-3 shrink-0 rounded-full border border-borde"
              }
              style={paletaControlada ? undefined : { backgroundColor: i.color ?? undefined }}
            />
            {i.nombre}
          </span>
        ) : (
          i.nombre
        ),
    },
    { encabezado: "Orden", render: (i) => i.orden },
    ...(mostrarResponsable
      ? [
          {
            encabezado: "Responsable",
            render: (i: ItemCatalogo) =>
              usuarios.find((u) => u.id === i.responsable_usuario_id)?.nombre_completo ?? "—",
          } satisfies ColumnaTabla<ItemCatalogo>,
        ]
      : []),
    {
      encabezado: "Estado",
      render: (i) => <Insignia texto={i.activo ? "Activo" : "Inactivo"} />,
    },
    {
      encabezado: "",
      render: (i) =>
        puedeAdministrar ? (
          <div className="flex justify-end gap-2">
            <Boton variante="secundario" onClick={() => abrirEdicion(i)}>
              Editar
            </Boton>
            <Boton variante="secundario" onClick={() => alternarActivo(i)}>
              {i.activo ? "Desactivar" : "Activar"}
            </Boton>
          </div>
        ) : null,
      className: "text-right",
    },
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-texto">{titulo}</h3>
          <p className="text-xs text-texto-secundario">{descripcion}</p>
        </div>
        {puedeAdministrar && <Boton onClick={abrirAlta}>+ Nuevo</Boton>}
      </div>

      <Tabla
        columnas={columnas}
        filas={items}
        claveFila={(i) => i.id}
        cargando={cargando}
        tituloVacio="Sin ítems"
      />

      <Modal
        titulo={enEdicion ? "Editar ítem" : "Nuevo ítem"}
        abierto={modalAbierto}
        onCerrar={() => setModalAbierto(false)}
      >
        <form onSubmit={guardar} className="space-y-4">
          <Campo etiqueta="Nombre" obligatorio>
            <Input
              required
              value={valores.nombre}
              onChange={(e) => setValores((v) => ({ ...v, nombre: e.target.value }))}
            />
          </Campo>
          <Campo etiqueta="Orden">
            <Input
              type="number"
              value={valores.orden}
              onChange={(e) => setValores((v) => ({ ...v, orden: e.target.value }))}
            />
          </Campo>
          {mostrarColor && paletaControlada && (
            <Campo etiqueta="Color">
              <SelectorColorSemantico
                valor={(valores.color || null) as ColorSemantico | null}
                onCambiar={(color) => setValores((v) => ({ ...v, color }))}
              />
            </Campo>
          )}
          {mostrarColor && !paletaControlada && (
            <Campo etiqueta="Color">
              <Input
                type="color"
                value={valores.color}
                onChange={(e) => setValores((v) => ({ ...v, color: e.target.value }))}
                className="h-10 w-20 p-1"
              />
            </Campo>
          )}
          {mostrarResponsable && (
            <Campo etiqueta="Responsable">
              <Select
                value={valores.responsable_usuario_id}
                onChange={(e) => setValores((v) => ({ ...v, responsable_usuario_id: e.target.value }))}
              >
                <option value="">Sin responsable</option>
                {usuarios.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nombre_completo}
                  </option>
                ))}
              </Select>
            </Campo>
          )}
          {mostrarDescripcion && (
            <Campo etiqueta="Descripción">
              <TextArea
                rows={3}
                value={valores.descripcion}
                onChange={(e) => setValores((v) => ({ ...v, descripcion: e.target.value }))}
              />
            </Campo>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Boton type="button" variante="secundario" onClick={() => setModalAbierto(false)} disabled={guardando}>
              Cancelar
            </Boton>
            <Boton type="submit" cargando={guardando}>
              Guardar
            </Boton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
